/**
 * PrintLog — print audit trail. Every label sent to the Tecor or the
 * browser is recorded (who / what / when / how many / how). Filterable
 * and CSV-exportable for traceability audits. Test/calibration prints
 * are never logged.
 */
import { useEffect, useMemo, useState } from "react";
import api, { withSite } from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import { enqueuePrint } from "@/lib/printHub";
import PageHeader from "@/components/PageHeader";
import ExportButton from "@/components/ExportButton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Printer, Usb, Monitor, RotateCcw, Trash2 } from "lucide-react";

const TYPE_LABEL = { prep: "Kitchen Prep", retail: "Retail", custom: "Custom Docket" };

function fmt(iso) {
  if (!iso) return "\u2014";
  return new Date(iso).toLocaleString("en-AU", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

export default function PrintLog() {
  const { activeSite } = useSite();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [reprinting, setReprinting] = useState("");
  const [reprintRow, setReprintRow] = useState(null);
  const [reprintCopies, setReprintCopies] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [copiesById, setCopiesById] = useState({});
  const [batchBusy, setBatchBusy] = useState(false);
  const [clearing, setClearing] = useState(false);

  const reload = async () => {
    const r = await api.get("/print-logs", withSite(activeSite.id));
    setLogs(r.data || []);
  };

  useEffect(() => {
    if (!activeSite) return;
    setLoading(true);
    api.get("/print-logs", withSite(activeSite.id))
      .then((r) => setLogs(r.data || []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [activeSite]);

  const clearTestRows = async () => {
    if (!window.confirm("Remove all demo rows (names starting with TEST_ or SEED)? This cannot be undone.")) return;
    setClearing(true);
    try {
      const { data } = await api.delete("/print-logs/test-rows", withSite(activeSite.id));
      toast.success(`Removed ${data.deleted} demo row${data.deleted === 1 ? "" : "s"}`);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't clear demo rows");
    } finally {
      setClearing(false);
    }
  };

  const openReprint = (row) => {
    if (!row.payload) {
      toast.error("This older print has no saved layout to reprint.");
      return;
    }
    setReprintRow(row);
    setReprintCopies(row.copies || 1);
  };

  const doReprint = async () => {
    const row = reprintRow;
    if (!row) return;
    const copies = Math.max(1, Math.min(Number(reprintCopies) || 1, 20));
    setReprinting(row.id);
    setReprintRow(null);
    try {
      await enqueuePrint(activeSite.id, {
        label_type: row.label_type,
        product_name: row.product_name,
        copies,
        payload: { ...row.payload, copies },
      });
      toast.success(`Queued ${row.product_name} ×${copies} to Print Hub`);
      const r = await api.get("/print-logs", withSite(activeSite.id));
      setLogs(r.data || []);
    } catch (err) {
      toast.error(err.message || "Reprint failed");
    } finally {
      setReprinting("");
    }
  };

  const filtered = useMemo(
    () => (filter === "all" ? logs : logs.filter((l) => l.label_type === filter)),
    [logs, filter],
  );

  const reprintable = useMemo(() => filtered.filter((l) => l.payload), [filtered]);

  const toggleRow = (id, defaultCopies) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setCopiesById((prev) => (prev[id] ? prev : { ...prev, [id]: defaultCopies || 1 }));
  };

  const setRowCopies = (id, val) => setCopiesById((prev) => ({ ...prev, [id]: val }));

  const allSelected = reprintable.length > 0 && reprintable.every((l) => selectedIds.has(l.id));
  const toggleAll = () => {
    if (allSelected) { setSelectedIds(new Set()); return; }
    setSelectedIds(new Set(reprintable.map((l) => l.id)));
    setCopiesById((prev) => {
      const next = { ...prev };
      reprintable.forEach((l) => { if (!next[l.id]) next[l.id] = l.copies || 1; });
      return next;
    });
  };

  const doBatchReprint = async () => {
    const rows = reprintable.filter((l) => selectedIds.has(l.id));
    if (!rows.length) return;
    setBatchBusy(true);
    let ok = 0;
    try {
      for (const row of rows) {
        const copies = Math.max(1, Math.min(Number(copiesById[row.id]) || row.copies || 1, 20));
        // eslint-disable-next-line no-await-in-loop
        await enqueuePrint(activeSite.id, {
          label_type: row.label_type, product_name: row.product_name, copies,
          payload: { ...row.payload, copies },
        });
        ok += 1;
      }
      toast.success(`Queued ${ok} job${ok === 1 ? "" : "s"} to Print Hub`);
      setSelectedIds(new Set());
      const r = await api.get("/print-logs", withSite(activeSite.id));
      setLogs(r.data || []);
    } catch (err) {
      toast.error(err.message || `Queued ${ok}, then failed`);
    } finally {
      setBatchBusy(false);
    }
  };

  const totalLabels = useMemo(
    () => filtered.reduce((s, l) => s + (l.copies || 1), 0),
    [filtered],
  );

  return (
    <div className="animate-fade-in" data-testid="print-log-page">
      <PageHeader
        title="Print Log"
        subtitle="Traceability record of every label printed — who, what, when and how many."
        action={
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button
                data-testid="print-log-clear-test"
                variant="outline"
                className="gap-2 text-red-700 border-red-200 hover:bg-red-50"
                disabled={clearing}
                onClick={clearTestRows}
              >
                <Trash2 className="w-4 h-4" /> {clearing ? "Clearing…" : "Clear test rows"}
              </Button>
            )}
            <ExportButton kind="print_logs" label="Export CSV" />
          </div>
        }
      />

      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="print-log-filter" className="h-10 w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All label types</SelectItem>
              <SelectItem value="prep">Kitchen Prep</SelectItem>
              <SelectItem value="retail">Retail</SelectItem>
              <SelectItem value="custom">Custom Docket</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-charcoal-soft" data-testid="print-log-summary">
            {filtered.length} print jobs · {totalLabels} labels
          </span>
        </div>
        {selectedIds.size > 0 && (
          <Button
            data-testid="print-log-reprint-selected"
            disabled={batchBusy}
            className="gap-2 bg-ochre-600 hover:bg-ochre-400 text-white"
            onClick={doBatchReprint}
          >
            <RotateCcw className="w-4 h-4" />
            {batchBusy ? "Queuing…" : `Reprint selected (${selectedIds.size})`}
          </Button>
        )}
      </div>

      <div className="surface overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-charcoal-soft text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-charcoal-soft text-sm" data-testid="print-log-empty">
            <Printer className="w-8 h-8 mx-auto mb-2 opacity-40" />
            No prints recorded yet. Print a label from the Label Printer page.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="print-log-table">
              <thead>
                <tr className="text-left border-b border-[color:var(--border)] text-charcoal-soft uppercase text-[11px] tracking-wider">
                  <th className="p-3 w-8">
                    <Checkbox
                      data-testid="print-log-select-all"
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      disabled={reprintable.length === 0}
                      aria-label="Select all reprintable"
                    />
                  </th>
                  <th className="p-3">Date / time</th>
                  <th className="p-3">Product</th>
                  <th className="p-3">Type</th>
                  <th className="p-3">Method</th>
                  <th className="p-3 text-center">Copies</th>
                  <th className="p-3">Printed by</th>
                  <th className="p-3 text-right">Reprint</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} className="border-b border-[color:var(--border)] last:border-0 hover:bg-eucalyptus-50" data-testid={`print-log-row-${l.id}`}>
                    <td className="p-3">
                      <Checkbox
                        data-testid={`print-log-select-${l.id}`}
                        checked={selectedIds.has(l.id)}
                        onCheckedChange={() => toggleRow(l.id, l.copies)}
                        disabled={!l.payload}
                        aria-label="Select for batch reprint"
                      />
                    </td>
                    <td className="p-3 font-mono text-xs whitespace-nowrap">{fmt(l.created_at)}</td>
                    <td className="p-3 font-medium">{l.product_name}</td>
                    <td className="p-3"><Badge variant="outline">{TYPE_LABEL[l.label_type] || l.label_type}</Badge></td>
                    <td className="p-3">
                      <span className="inline-flex items-center gap-1 text-xs">
                        {l.method === "tecor" ? <Usb className="w-3 h-3" /> : <Monitor className="w-3 h-3" />}
                        {l.method === "tecor" ? "Tecor" : "Browser"}
                      </span>
                    </td>
                    <td className="p-3 text-center font-mono">
                      {selectedIds.has(l.id) && l.payload ? (
                        <Input
                          data-testid={`print-log-copies-${l.id}`}
                          type="number"
                          min="1"
                          max="20"
                          value={copiesById[l.id] ?? (l.copies || 1)}
                          onChange={(e) => setRowCopies(l.id, e.target.value)}
                          className="h-8 w-16 mx-auto text-center font-mono"
                          title="Copies to reprint"
                        />
                      ) : (
                        l.copies || 1
                      )}
                    </td>
                    <td className="p-3">{l.user_name}</td>
                    <td className="p-3 text-right">
                      <Button
                        data-testid={`print-log-reprint-${l.id}`}
                        size="sm"
                        variant="outline"
                        className="gap-1 h-8 border-ochre-600 text-ochre-700 hover:bg-ochre-50"
                        disabled={!l.payload || reprinting === l.id}
                        title={
                          !l.payload
                            ? "No saved layout for this older print"
                            : "Send this exact label to the Print Hub again"
                        }
                        onClick={() => openReprint(l)}
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        {reprinting === l.id ? "Queuing…" : "Reprint"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reprint quantity dialog */}
      <Dialog open={!!reprintRow} onOpenChange={(o) => { if (!o) setReprintRow(null); }}>
        <DialogContent data-testid="reprint-dialog">
          <DialogHeader>
            <DialogTitle>Reprint label</DialogTitle>
            <DialogDescription>
              {reprintRow ? `Send "${reprintRow.product_name}" to the Print Hub again. Choose how many copies.` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reprint-copies" className="text-xs">Copies</Label>
            <Input
              id="reprint-copies"
              data-testid="reprint-copies"
              type="number"
              min="1"
              max="20"
              className="h-11 w-28 font-mono text-center"
              value={reprintCopies}
              onChange={(e) => setReprintCopies(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") doReprint(); }}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReprintRow(null)}>Cancel</Button>
            <Button data-testid="reprint-confirm" className="gap-1 bg-ochre-600 hover:bg-ochre-400 text-white" onClick={doReprint}>
              <RotateCcw className="w-4 h-4" /> Reprint
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
